// react-redux imports
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { useForm } from "react-hook-form";
import { setAuthData } from "features/auth/authSlice";
import { SeparatedWaste } from "features/calculator/householdHabits/householdHabitsSlice";
import { RootState } from "app/store";

import {
  CalculatorResult,
  AdminAuthResponse,
  UserAuthResponse,
} from "features/auth/types";

import { EMAIL_PATTERN } from "common/constants/regex";
import { API_URL } from "common/constants/config";
import FlexColumn from "common/components/styled-components/FlexColumn";
import Input from "common/components/Input";

import ActionButton from "../styled-components/ActionButton";
import ActionGroup from "../styled-components/ActionGroup";
import Form from "../styled-components/Form";
import FormTitle from "./styled-components/FormTitle";
import TermsConfirmation from "../TermsConfirmation";

interface SignInFormProps {
  sendCalculatorData?: boolean;
  successCallback(resultResponse: CalculatorResult): void;
}

interface FormFields {
  email: string;
  password: string;
  acceptTerms: boolean;
}

function SignInForm({
  sendCalculatorData = true,
  successCallback,
}: SignInFormProps) {
  const dispatch = useDispatch();
  const history = useHistory();
  const calculator = useSelector((state: RootState) => state.calculator);

  const {
    register,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm<FormFields>();

  async function submitCallback(data: FormFields) {
    try {
      const {
        householdHabits: { separatedWaste: separatedWasteObject },
      } = calculator;

      const separatedWaste = Object.entries(separatedWasteObject).reduce(
        (acc: Array<keyof SeparatedWaste>, cur: [string, boolean]) => {
          const [key, value] = cur;
          if (value) acc.push(key as keyof SeparatedWaste);
          return acc;
        },
        []
      );

      const payload = sendCalculatorData
        ? {
            email: data.email,
            password: data.password,
            calculator: {
              ...calculator,
              householdHabits: {
                ...calculator.householdHabits,
                separatedWaste,
              },
            },
          }
        : {
            email: data.email,
            password: data.password,
          };
      const response = await axios.post<UserAuthResponse | AdminAuthResponse>(
        `${API_URL}/auth/sign-in`,
        payload
      );

      const isAdmin = Object.keys(response.data)[0] === "admin";
      if (isAdmin) {
        const {
          admin: { email },
        } = response.data as AdminAuthResponse;
        dispatch(setAuthData({ email, name: "admin", role: "admin" }));
        history.replace("/admin");
      } else {
        const {
          user: { email, name, result },
        } = response.data as UserAuthResponse;
        dispatch(
          setAuthData({
            role: "user",
            email,
            name,
          })
        );
        successCallback(result);
      }
    } catch (err: any) {
      switch (err?.response?.data?.code) {
        case "404-002":
          setError(
            "email",
            {
              type: "emailNotFound",
              message: "Correo electrónico no encontrado",
            },
            { shouldFocus: true }
          );
          break;

        case "401-004":
          setError(
            "password",
            {
              type: "wrongPassword",
              message: "Contraseña incorrecta",
            },
            { shouldFocus: true }
          );
          break;
      }
    }
  }

  return (
    <Form onSubmit={handleSubmit(submitCallback)} autoComplete="off">
      <FormTitle>Inicia sesión</FormTitle>
      <FlexColumn gap={8}>
        <Input
          registerReturn={register("email", {
            required: "Introduce tu correo electrónico",
            pattern: {
              value: EMAIL_PATTERN,
              message: "Correo electrónico inválido",
            },
          })}
          error={errors.email}
          placeholder="Correo electrónico*"
        />
        <Input
          registerReturn={register("password", {
            required: "Introduce tu contraseña",
          })}
          error={errors.password}
          type="password"
          placeholder="Contraseña*"
        />
      </FlexColumn>
      <ActionGroup>
        <TermsConfirmation
          registerReturn={register("acceptTerms", { required: true })}
        />
        <ActionButton type="submit">Iniciar sesión</ActionButton>
      </ActionGroup>
    </Form>
  );
}

export default SignInForm;
